import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/", match: (p: string) => p === "/" },
  { label: "电影", to: "/category?type=movie", match: (p: string, s: string) => p === "/category" && s.includes("type=movie") },
  { label: "电视剧", to: "/category?type=tv", match: (p: string, s: string) => p === "/category" && s.includes("type=tv") },
  { label: "专题", to: "/category?topic=oscar", match: (p: string, s: string) => p === "/category" && s.includes("topic=") },
  { label: "关于我们", to: "/about", match: (p: string) => p === "/about" },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = () => {
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-800 bg-background-950/95 backdrop-blur"
          : "border-transparent bg-background-950/70 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500">
            <i className="ri-film-fill text-lg text-foreground-50" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold text-foreground-50">欧美精品</span>
            <span className="text-[10px] tracking-[0.2em] text-accent-400">ONLINE CINEMA</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          {NAV_ITEMS.map((item) => {
            const active = item.match(location.pathname, location.search);
            return (
              <Link
                key={item.label}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  active
                    ? "text-accent-400 font-medium"
                    : "text-foreground-300 hover:text-foreground-50"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto hidden md:flex items-center">
          <div className="flex items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-1.5 focus-within:border-primary-500">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              placeholder="搜索影片、剧集、演员"
              className="w-40 lg:w-56 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
              aria-label="站内搜索"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="rounded bg-primary-500 px-2 py-0.5 text-xs text-foreground-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开导航菜单"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-100 md:hidden cursor-pointer"
        >
          <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {mobileOpen ? (
        <div className="md:hidden border-t border-background-800 bg-background-950 px-4 pb-5 pt-4">
          <div className="flex items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-2 focus-within:border-primary-500">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              placeholder="搜索影片、剧集、演员"
              className="flex-1 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
              aria-label="站内搜索"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="rounded bg-primary-500 px-3 py-1 text-xs text-foreground-50 whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </div>

          <nav className="mt-4 flex flex-col">
            {NAV_ITEMS.map((item) => {
              const active = item.match(location.pathname, location.search);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`rounded-md px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                    active ? "bg-background-900 text-accent-400" : "text-foreground-200"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      ) : null}
    </header>
  );
}